import { createBrowserClient as createSsrBrowserClient } from '@supabase/ssr';
import { createClient, SupabaseClient } from '@supabase/supabase-js';

// ==========================================
// TIPOS DO BANCO DE DADOS
//
// NOTA: Frontend usa tabelas UNIFIED (novas):
// - conversations, messages, contacts (schema moderno)
//
// Backend (brain/worker) também usa as tabelas UNIFIED.
//
// Ver: docs/architecture/DATABASE_SCHEMA_REFACTOR.md
// ==========================================

export type Json =
  | string
  | number
  | boolean
  | null
  | { [key: string]: Json | undefined }
  | Json[]

export interface Database {
  public: {
    Tables: {
      profiles: {
        Row: {
          id: string
          email: string
          business_name: string | null
          whatsapp: string | null
          plan: 'free' | 'starter' | 'pro' | 'enterprise' | null
          plan_started_at: string | null
          stripe_customer_id: string | null
          stripe_subscription_id: string | null
          is_support_admin: boolean | null
          created_at: string | null
          updated_at: string | null
        }
        Insert: {
          id: string
          email: string
          business_name?: string | null
          whatsapp?: string | null
          plan?: 'free' | 'starter' | 'pro' | 'enterprise' | null
          plan_started_at?: string | null
          stripe_customer_id?: string | null
          stripe_subscription_id?: string | null
          is_support_admin?: boolean | null
          created_at?: string | null
          updated_at?: string | null
        }
        Update: {
          id?: string
          email?: string
          business_name?: string | null
          whatsapp?: string | null
          plan?: 'free' | 'starter' | 'pro' | 'enterprise' | null
          plan_started_at?: string | null
          stripe_customer_id?: string | null
          stripe_subscription_id?: string | null
          is_support_admin?: boolean | null
          updated_at?: string | null
        }
      }
      posts: {
        Row: {
          id: string
          created_at: string
          slug: string
          title: string
          excerpt: string | null
          content: string
          cover_image: string | null
          published_at: string
          reading_time: number
          author_name: string | null
          author_avatar: string | null
        }
        Insert: {
          id?: string
          created_at?: string
          slug: string
          title: string
          excerpt?: string | null
          content: string
          cover_image?: string | null
          published_at?: string
          reading_time?: number
          author_name?: string | null
          author_avatar?: string | null
        }
        Update: {
          id?: string
          created_at?: string
          slug?: string
          title?: string
          excerpt?: string | null
          content?: string
          cover_image?: string | null
          published_at?: string
          reading_time?: number
          author_name?: string | null
          author_avatar?: string | null
        }
        Relationships: []
      }
      feedback: {
        Row: {
          id: string
          created_at: string
          user_id: string
          type: string
          subject: string
          message: string
          rating: number | null
          status: string
        }
        Insert: {
          id?: string
          created_at?: string
          user_id: string
          type: string
          subject: string
          message: string
          rating?: number | null
          status?: string
        }
        Update: {
          id?: string
          created_at?: string
          user_id?: string
          type?: string
          subject?: string
          message?: string
          rating?: number | null
          status?: string
        }
        Relationships: []
      }
      business_context: {
        Row: {
          id: string
          user_id: string
          business_type: string | null
          business_description: string | null
          tone_of_voice: string | null
          services: Json | null
          working_hours: Json | null
          common_questions: Json | null
          custom_instructions: string | null
          voice_settings: Json | null
          created_at: string | null
          updated_at: string | null
        }
        Insert: {
          id?: string
          user_id: string
          business_type?: string | null
          business_description?: string | null
          tone_of_voice?: string | null
          services?: Json | null
          working_hours?: Json | null
          common_questions?: Json | null
          custom_instructions?: string | null
          voice_settings?: Json | null
          created_at?: string | null
          updated_at?: string | null
        }
        Update: {
          user_id?: string
          business_type?: string | null
          business_description?: string | null
          tone_of_voice?: string | null
          services?: Json | null
          working_hours?: Json | null
          common_questions?: Json | null
          custom_instructions?: string | null
          voice_settings?: Json | null
          updated_at?: string | null
        }
      }
      whatsapp_sessions: {
        Row: {
          id: string
          user_id: string
          phone_number: string | null
          status: 'disconnected' | 'connecting' | 'qr_pending' | 'connected' | 'error' | 'conflict'
          qr_code: string | null
          session_data: Json | null
          error_message: string | null
          last_connected_at: string | null
          ai_enabled: boolean
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          user_id: string
          phone_number?: string | null
          status?: 'disconnected' | 'connecting' | 'qr_pending' | 'connected' | 'error' | 'conflict'
          qr_code?: string | null
          session_data?: Json | null
          error_message?: string | null
          last_connected_at?: string | null
          ai_enabled?: boolean
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          user_id?: string
          phone_number?: string | null
          status?: 'disconnected' | 'connecting' | 'qr_pending' | 'connected' | 'error' | 'conflict'
          qr_code?: string | null
          session_data?: Json | null
          error_message?: string | null
          last_connected_at?: string | null
          ai_enabled?: boolean
          updated_at?: string
        }
      }
      contacts: {
        Row: {
          id: string
          session_id: string
          remote_jid: string
          user_id: string
          display_name: string | null
          push_name: string | null
          phone_number: string | null
          is_business: boolean
          avatar_url: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          session_id: string
          remote_jid: string
          user_id?: string
          display_name?: string | null
          push_name?: string | null
          phone_number?: string | null
          is_business?: boolean
          avatar_url?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          session_id?: string
          remote_jid?: string
          user_id?: string
          display_name?: string | null
          push_name?: string | null
          phone_number?: string | null
          is_business?: boolean
          avatar_url?: string | null
          updated_at?: string
        }
      }
      conversations: {
        Row: {
          id: string
          session_id: string
          remote_jid: string
          user_id: string
          title: string | null
          conversation_type: 'individual' | 'group'
          contact_id: string | null
          ai_enabled: boolean
          last_message_at: string | null
          message_count: number
          unread_count: number
          ai_summary: string | null
          photo_hash: string | null
          avatar_url: string | null
          avatar_fetched_at: string | null
          created_at: string
        }
        Insert: {
          id?: string
          session_id: string
          remote_jid: string
          user_id?: string
          title?: string | null
          conversation_type?: 'individual' | 'group'
          contact_id?: string | null
          ai_enabled?: boolean
          last_message_at?: string | null
          message_count?: number
          unread_count?: number
          ai_summary?: string | null
          photo_hash?: string | null
          avatar_url?: string | null
          avatar_fetched_at?: string | null
          created_at?: string
        }
        Update: {
          session_id?: string
          remote_jid?: string
          user_id?: string
          title?: string | null
          conversation_type?: 'individual' | 'group'
          contact_id?: string | null
          ai_enabled?: boolean
          last_message_at?: string | null
          message_count?: number
          unread_count?: number
          ai_summary?: string | null
          photo_hash?: string | null
          avatar_url?: string | null
          avatar_fetched_at?: string | null
        }
      }
      messages: {
        Row: {
          id: string
          conversation_id: string
          message_id: string
          user_id: string
          sender_jid: string
          direction: 'incoming' | 'outgoing'
          content: string | null
          message_type: string
          metadata: Json | null
          timestamp: string
          message_timestamp: number
          ai_processed: boolean
          ai_response: string | null
          media_url: string | null
          delivery_status: 'pending' | 'sent' | 'delivered' | 'read' | 'failed' | null
          is_history_sync: boolean
          created_at: string
        }
        Insert: {
          id?: string
          conversation_id: string
          message_id: string
          user_id?: string
          sender_jid: string
          direction: 'incoming' | 'outgoing'
          content?: string | null
          message_type?: string
          metadata?: Json | null
          timestamp?: string
          message_timestamp?: number
          ai_processed?: boolean
          ai_response?: string | null
          media_url?: string | null
          delivery_status?: 'pending' | 'sent' | 'delivered' | 'read' | 'failed' | null
          is_history_sync?: boolean
          created_at?: string
        }
        Update: {
          conversation_id?: string
          message_id?: string
          user_id?: string
          sender_jid?: string
          direction?: 'incoming' | 'outgoing'
          content?: string | null
          message_type?: string
          metadata?: Json | null
          timestamp?: string
          message_timestamp?: number
          ai_processed?: boolean
          ai_response?: string | null
          media_url?: string | null
          delivery_status?: 'pending' | 'sent' | 'delivered' | 'read' | 'failed' | null
          is_history_sync?: boolean
        }
      }
      whatsapp_outgoing_messages: {
        Row: {
          id: string
          conversation_id: string
          message_id: string | null
          content: string | null
          content_type: string | null
          media_url: string | null
          priority: string | null
          status: string
          created_at: string | null
          updated_at: string | null
        }
        Insert: {
          id?: string
          conversation_id: string
          message_id?: string | null
          content?: string | null
          content_type?: string | null
          media_url?: string | null
          priority?: string | null
          status?: string
          created_at?: string | null
          updated_at?: string | null
        }
        Update: {
          id?: string
          conversation_id?: string
          message_id?: string | null
          content?: string | null
          content_type?: string | null
          media_url?: string | null
          priority?: string | null
          status?: string
          updated_at?: string | null
        }
      }
      support_tickets: {
        Row: {
          id: string
          user_id: string
          status: 'open' | 'waiting' | 'closed'
          subject: string | null
          last_message_at: string
          created_at: string
          updated_at: string
          closed_at: string | null
        }
        Insert: {
          id?: string
          user_id: string
          status?: 'open' | 'waiting' | 'closed'
          subject?: string | null
          last_message_at?: string
          created_at?: string
          updated_at?: string
          closed_at?: string | null
        }
        Update: {
          id?: string
          user_id?: string
          status?: 'open' | 'waiting' | 'closed'
          subject?: string | null
          last_message_at?: string
          updated_at?: string
          closed_at?: string | null
        }
      }
      support_messages: {
        Row: {
          id: string
          ticket_id: string
          sender_id: string
          sender_type: 'customer' | 'agent'
          content: string
          read_at: string | null
          created_at: string
        }
        Insert: {
          id?: string
          ticket_id: string
          sender_id: string
          sender_type: 'customer' | 'agent'
          content: string
          read_at?: string | null
          created_at?: string
        }
        Update: {
          id?: string
          ticket_id?: string
          sender_id?: string
          sender_type?: 'customer' | 'agent'
          content?: string
          read_at?: string | null
        }
      }
    }
    Views: {
      [_ in never]: never
    }
    Functions: {
      get_conversations_with_contact_names: {
        Args: {
          p_user_id: string
          p_limit?: number
          p_offset?: number
        }
        Returns: {
          id: string
          session_id: string
          remote_jid: string
          contact_name: string
          contact_phone: string
          avatar_url: string
          channel: string
          status: string
          last_message_at: string
          message_count: number
          unread_count: number
          ai_summary: string
          ai_enabled: boolean
          created_at: string
        }[]
      }
      approve_discovered_pattern: {
        Args: {
          p_pattern_id: string
        }
        Returns: undefined
      }
      reject_discovered_pattern: {
        Args: {
          p_pattern_id: string
        }
        Returns: undefined
      }
    }
    Enums: {
      [_ in never]: never
    }
    CompositeTypes: {
      [_ in never]: never
    }
  }
}

// ==========================================
// CLIENTE SUPABASE
// ==========================================

const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || '';
const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || '';

let supabaseInstance: SupabaseClient<Database> | null = null;

export function getSupabase(): SupabaseClient<Database> {
  if (!supabaseInstance && supabaseUrl && supabaseAnonKey) {
    supabaseInstance = createClient<Database>(supabaseUrl, supabaseAnonKey);
  }
  if (!supabaseInstance) {
    throw new Error('Supabase not configured. Set NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_ANON_KEY');
  }
  return supabaseInstance;
}

// Para uso direto (backward compatibility)
export const supabase = supabaseUrl && supabaseAnonKey
  ? createClient<Database>(supabaseUrl, supabaseAnonKey)
  : null;

// ==========================================
// CLIENTE PARA BROWSER (Auth)
// ==========================================

let browserClient: SupabaseClient<Database> | null = null;

// Custom error class to distinguish configuration errors from network errors
export class SupabaseConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SupabaseConfigError';
  }
}

export function createBrowserClient(): SupabaseClient<Database> {
  if (browserClient) return browserClient;

  console.log('[Debug] Initializing Supabase Client...');
  console.log('[Debug] URL:', supabaseUrl);
  console.log('[Debug] Key length:', supabaseAnonKey?.length);

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new SupabaseConfigError('Supabase not configured. Please check environment variables.');
  }

  // Importante: usar o client do @supabase/ssr no browser, para que a sessão
  // seja persistida também via cookies (lidos em Route Handlers / Server Components).
  browserClient = createSsrBrowserClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      flowType: 'pkce',
      detectSessionInUrl: true,
      persistSession: true,
      autoRefreshToken: true,
    }
  }) as unknown as SupabaseClient<Database>;

  return browserClient;
}

// ==========================================
// CLIENTE PARA SERVER (API Routes)
// ==========================================

let serverClient: SupabaseClient<Database> | null = null;

export function createServerClient(): SupabaseClient<Database> {
  if (serverClient) return serverClient;

  const supabaseServiceKey = process.env.SUPABASE_SERVICE_ROLE_KEY;

  if (!supabaseUrl || !supabaseServiceKey) {
    throw new SupabaseConfigError('Server Supabase not configured. Please check SUPABASE_SERVICE_ROLE_KEY environment variable.');
  }

  serverClient = createClient<Database>(supabaseUrl, supabaseServiceKey, {
    auth: {
      autoRefreshToken: false,
      persistSession: false,
    }
  });

  return serverClient;
}

// ==========================================
// HELPER TYPES
// ==========================================

export type Tables<T extends keyof Database['public']['Tables']> = Database['public']['Tables'][T]['Row'];
export type TablesInsert<T extends keyof Database['public']['Tables']> = Database['public']['Tables'][T]['Insert'];
export type TablesUpdate<T extends keyof Database['public']['Tables']> = Database['public']['Tables'][T]['Update'];

// Convenience types
export type Profile = Tables<'profiles'>;
export type BusinessContext = Tables<'business_context'>;
export type Conversation = Tables<'conversations'>;
export type Message = Tables<'messages'>;
export type Contact = Tables<'contacts'>;
export type WhatsAppSession = Tables<'whatsapp_sessions'>;
